import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {
  ApplicationConfig,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  inject
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
// Deep imports on purpose: the generated `@bpp-stella/api/*` barrel re-exports every api
// service, so a barrel import here would put all of them in the initial chunk (they are only
// tree-shaken when nothing else uses them). `provide-api` only carries BASE_PATH/Configuration.
import { provideApi as provideAuthApi } from '@bpp-stella/api/auth/provide-api';
import { provideApi as provideStellaApi } from '@bpp-stella/api/stella/provide-api';
import { provideApi as provideFileApi } from '@bpp-stella/api/file/provide-api';
import { provideApi as provideChatApi } from '@bpp-stella/api/chat/provide-api';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { KundenportalPreset } from '../styles/kundenportal-preset';
import { TranslocoBundleLoader } from './core/i18n/transloco-loader';
import { APP_LANGUAGES, LanguageService, resolveInitialLanguage } from './core/i18n/language.service';
import { ThemeService } from './core/theme/theme.service';
import { KundenportalVersionService } from './core/config/kundenportal-version.service';
import { authTokenInterceptor } from './core/auth/auth-token.interceptor';
import { devLoginInitializer } from './core/auth/dev-login.initializer';
import { DevLoginExpiryService } from './core/auth/dev-login-expiry.service';

// Dates and numbers render German ("01.03.2026", "1.284,50") in both UI languages, like the
// mobile app; the API delivers ISO strings and plain numbers. Amounts go through `EuroPipe`,
// which leads with the symbol ("€ 1.284,50") — the `de` currency format would trail it.
registerLocaleData(localeDe, 'de');

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    // Every navigation, back/forward included, starts at the top of the page (BRO-1245).
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideHttpClient(withInterceptors([authTokenInterceptor])),
    provideAuthApi(environment.bppAuth),
    provideStellaApi(environment.bppStella),
    provideFileApi(environment.bppFile),
    provideChatApi(environment.bppChat),
    { provide: LOCALE_ID, useValue: 'de' },
    // One toast outlet for the whole portal (`<p-toast>` in the shell); today only foreground pushes use it.
    MessageService,
    providePrimeNG({
      theme: {
        preset: KundenportalPreset,
        options: {
          darkModeSelector: '.p-dark'
        }
      }
    }),
    provideTransloco({
      config: {
        availableLangs: [...APP_LANGUAGES],
        defaultLang: resolveInitialLanguage(),
        fallbackLang: 'DE',
        prodMode: environment.production,
        reRenderOnLangChange: true
      },
      loader: TranslocoBundleLoader
    }),
    // Developer login as a Stella person (`?dev_login_code=`, bpp-auth dashboard): consumed before
    // the router's first navigation; the expiry watch covers sessions restored on reload too.
    provideAppInitializer(async () => {
      const devLoginExpiry = inject(DevLoginExpiryService);
      await devLoginInitializer();
      // Only now: a code arriving over an expired dev session logs that session out itself, and
      // the watch's own logout would navigate before the router's first navigation and replace it.
      devLoginExpiry.start();
    }),
    provideAppInitializer(() => {
      inject(ThemeService).init();
      inject(LanguageService).init();

      // Forced reset on a Kundenportal deploy (ui_configs row `kundenportal`): fire-and-forget, never blocks boot.
      inject(KundenportalVersionService).start();

      // Browser-tab title follows the active language (index.html only carries the fallback).
      const title = inject(Title);
      inject(TranslocoService)
        .selectTranslate('app.title')
        .subscribe((translated) => title.setTitle(translated));
    })
  ]
};
